/**
 * The discovery layout for one BOM line, from its normalized listings: the
 * main list (listings matching the line's spec, one entry per supplier) and
 * the alternatives (listings offering another spec, grouped by the value they
 * offer — 40C for a 30C line). Each list splits into Robbie's #1/#2 and the
 * rest the same way, so a preview of another spec reads like the line's own.
 * Pure, no React.
 *
 * The backend reads every listing against the line (`specGroup`, `supplierKey`,
 * `listingId`); a payload from before it did is read off `specMismatches` and
 * the supplier name, so nothing breaks while the two deploy.
 */
import { SpecMismatchView, SupplierView } from './discoveryModel';

/** One supplier in a list: every listing it has there, its best one first. */
export interface SupplierGroup {
  key: string;
  name: string;
  listings: SupplierView[];
  /** The listing the card leads with and Robbie's picks preselect. */
  best: SupplierView;
  rank?: number;
}

export type AlternativeKind = 'exceeds' | 'below' | 'different';

/** One spec's suppliers, split the way the pane shows them. */
export interface RankedSplit {
  /** Robbie's picks: at most two suppliers, ranked 1–2. */
  picks: SupplierGroup[];
  others: SupplierGroup[];
  ranked: boolean;
}

/** Listings offering the same other spec. */
export interface AlternativeGroup extends RankedSplit {
  specGroup: string;
  changes: SpecMismatchView[];
  /** exceeds only when every change is a higher rating than asked. */
  kind: AlternativeKind;
  suppliers: SupplierGroup[];
  fromPrice?: { amount: number; currencySymbol: string };
}

export interface DiscoveryLayout extends RankedSplit {
  main: SupplierGroup[];
  alternatives: AlternativeGroup[];
}

const specGroupOf = (s: SupplierView): string =>
  typeof s.specGroup === 'string'
    ? s.specGroup
    : (s.specMismatches ?? []).map((m) => `${m.key}=${m.offered}`).sort().join('|');

/** The listing's id for the shortlist; the name when the payload carries none. */
export const listingKeyOf = (s: SupplierView): string => s.listingId ?? s.name;

const rankOf = (s: SupplierView): number => s.rank ?? Infinity;
const priceOf = (s: SupplierView): number => (s.quotedPrice != null && s.quotedPrice > 0 ? s.quotedPrice : Infinity);
const partNumberOrder = (s: SupplierView): number =>
  s.partNumberCheck?.status === 'match' ? 0 : s.partNumberCheck?.status === 'mismatch' ? 2 : 1;

/** Robbie's ranked listing first, then the best part-number match, then the cheapest. */
const byBest = (a: SupplierView, b: SupplierView): number =>
  rankOf(a) - rankOf(b) || partNumberOrder(a) - partNumberOrder(b) || priceOf(a) - priceOf(b);

/** Listings → one group per supplier, in the order their best listing ranks. */
export const groupBySupplier = (listings: SupplierView[]): SupplierGroup[] => {
  const groups = new Map<string, SupplierView[]>();
  listings.forEach((s) => {
    const key = s.supplierKey ?? `name:${s.name.toLowerCase()}`;
    groups.set(key, [...(groups.get(key) ?? []), s]);
  });
  return Array.from(groups.entries())
    .map(([key, members]) => {
      const sorted = [...members].sort(byBest);
      return { key, name: sorted[0].name, listings: sorted, best: sorted[0], rank: sorted[0].rank };
    })
    .sort((a, b) => byBest(a.best, b.best));
};

/** exceeds when every change is a higher rating; different when any must match exactly (or is unlabelled); else below. */
const kindOf = (changes: SpecMismatchView[]): AlternativeKind => {
  if (changes.every((c) => c.kind === 'exceeds')) return 'exceeds';
  return changes.some((c) => !c.kind || c.kind === 'different') ? 'different' : 'below';
};

const KIND_ORDER: Record<AlternativeKind, number> = { exceeds: 0, below: 1, different: 2 };

/** Ranked groups → Robbie's #1/#2 and the rest; unranked → everything in the rest. */
export const splitRanked = (groups: SupplierGroup[]): RankedSplit => {
  const ranked = groups.some((g) => g.rank != null);
  const picks = ranked ? groups.filter((g) => (g.rank ?? Infinity) <= 2).slice(0, 2) : [];
  return { picks, others: groups.filter((g) => !picks.includes(g)), ranked };
};

const cheapestOf = (listings: SupplierView[]) => {
  const cheapest = listings.filter((s) => priceOf(s) !== Infinity).sort((a, b) => priceOf(a) - priceOf(b))[0];
  return cheapest ? { amount: cheapest.quotedPrice as number, currencySymbol: cheapest.currencySymbol } : undefined;
};

const alternativesOf = (listings: SupplierView[]): AlternativeGroup[] => {
  const bySpec = new Map<string, SupplierView[]>();
  listings.forEach((s) => bySpec.set(specGroupOf(s), [...(bySpec.get(specGroupOf(s)) ?? []), s]));
  return Array.from(bySpec.entries())
    .map(([specGroup, members]) => {
      const changes = members[0].specMismatches ?? [];
      const suppliers = groupBySupplier(members);
      return { specGroup, changes, kind: kindOf(changes), suppliers, fromPrice: cheapestOf(members), ...splitRanked(suppliers) };
    })
    .sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || (a.fromPrice?.amount ?? Infinity) - (b.fromPrice?.amount ?? Infinity));
};

/** The whole layout for one line. */
export const discoveryLayoutOf = (listings: SupplierView[]): DiscoveryLayout => {
  const main = groupBySupplier(listings.filter((s) => specGroupOf(s) === ''));
  return { main, ...splitRanked(main), alternatives: alternativesOf(listings.filter((s) => specGroupOf(s) !== '')) };
};

/** The line's cheapest matching price, for comparing it with an alternative's. */
export const mainFromPriceOf = (layout: DiscoveryLayout) => cheapestOf(layout.main.flatMap((g) => g.listings));

/** "30C" — the line's values the alternatives differ from, as one phrase. */
export const requestedSpecOf = (alternatives: AlternativeGroup[]): string =>
  Array.from(new Set(alternatives.flatMap((a) => a.changes.map((c) => c.requested)))).join(' ');

/** What a product row is called: the spec its page states, else its page's slug. */
export const productLabelOf = (s: SupplierView, index: number): string => {
  const offered = s.specMatch?.find((r) => r.offered)?.offered;
  if (offered) return offered;
  if (s.websiteKind === 'product' && s.websiteUrl) {
    const slug = new URL(s.websiteUrl).pathname.split('/').filter(Boolean).pop() ?? '';
    const words = decodeURIComponent(slug).replace(/\.[a-z]+$/i, '').replace(/[-_]+/g, ' ').trim();
    if (words) return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return `Product ${index + 1}`;
};

/** How many suppliers the ticked listings belong to (a supplier with two ticked products counts once). */
export const pickedSupplierCount = (listings: SupplierView[], tickedIds: readonly string[]): number =>
  new Set(listings.filter((s) => tickedIds.includes(listingKeyOf(s))).map((s) => s.supplierKey ?? s.name)).size;
